import React from 'react';

import { TableCell, TableRow, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from '../../../../constants/date.constant';
import { SalesDetailsHelper } from '../../../../helpers/sales-details.helper';
import { ISalesDetails } from '../../../../types/sales-details.type';
import { ISales } from '../../../../types/sales.type';
import { ArrayUtils } from '../../../../utils/array.utils';
import { DateUtils } from '../../../../utils/date.utils';
import { NumberUtils } from '../../../../utils/number.utils';
import { SALES_DETAILS_PAYMENTS } from '../../constants';
import SalesDetailsListRegistration from '../SalesDetailsListRegistration';

const numberKeys: (keyof ISales)[] = [
  'total',
  'coco',
  'dise',
  'certificate',
  'preInscription',
  'books',
  'fees',
  'delivery',
  'discounts',
  'verify',
];

interface IProps {
  detail: ISalesDetails;
}

const SalesDetailsListItem = React.memo(({ detail }: IProps) => {
  const { t } = useTranslation();

  const date = dayjs(detail.date);
  const year = date.format('YYYY');

  const renderPayment = (number: number) => {
    const key = `${detail.number}_payment_${number}`;

    const payment = detail.payments?.find(({ sequenceNumber }) => sequenceNumber === number);

    if (!payment) {
      return <TableCell key={key} />;
    }

    const backgroundColor = SalesDetailsHelper.getPaymentColor(payment);

    if (payment.paidDate) {
      return (
        <Tooltip
          key={key}
          title={dayjs(payment.paidDate).format(DATE_FORMAT)}
          placement="top"
          arrow
        >
          <TableCell sx={{ backgroundColor }}>{payment.amount}</TableCell>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        key={key}
        title={payment.dueDate ? dayjs(payment.dueDate).format(DATE_FORMAT) : null}
        placement="top"
        arrow
      >
        <TableCell key={key} sx={{ backgroundColor }}>
          {payment.amount}
        </TableCell>
      </Tooltip>
    );
  };

  return (
    <>
      <TableRow sx={{ '& td': { textAlign: 'center' } }}>
        <TableCell>{t(`school.${detail.school}`)}</TableCell>
        <TableCell>{detail.schoolYear}</TableCell>
        <TableCell>{date.format(DATE_FORMAT)}</TableCell>
        <TableCell>{detail.number}</TableCell>

        <TableCell>
          {DateUtils.getWeekOfYear(date)} - {year}
        </TableCell>

        <TableCell>
          {DateUtils.getMonthName(date)} - {year}
        </TableCell>

        <TableCell>{detail.customer}</TableCell>
        <TableCell>{detail.email}</TableCell>
        <TableCell>{detail.deliveryCountry}</TableCell>

        {ArrayUtils.makeRange(7).map((number) => (
          <TableCell key={`${detail.number}_empty_${number}`} />
        ))}

        {numberKeys.map((key) => (
          <TableCell key={`${detail.customer}_${key}`}>
            {NumberUtils.toCurrency(detail[key] as number)}
          </TableCell>
        ))}

        <TableCell>{detail.payments?.length || 0}</TableCell>

        {ArrayUtils.makeRange(SALES_DETAILS_PAYMENTS).map((number) => renderPayment(number))}
      </TableRow>

      {detail.registrations.map((registration) => (
        <SalesDetailsListRegistration
          detail={detail}
          registration={registration}
          key={`${detail.number}_${registration.id}`}
        />
      ))}
    </>
  );
});

export default SalesDetailsListItem;
